/**
 * **سجلّ العميل** (0151، قرار المالك 01/10/2026: «حالات المتابعات للعميل الواحد تتسجّل وتتحدّث ويبقى فيه زي history
 * بيتحدّث مع كل متابعة»): كل تواصلٍ مع العميل حلقةٌ في خطٍّ واحد — الأحدث فوق — بيومه ونوعه ونتيجته وموقف العميل
 * وملاحظاته، واليوم الذي بعده — كلٌّ «تحديثٌ» لموقف المتابعة نفسها (قرار المالك: «بدل سجل جديد، خلّيها تحديث موقف
 * المتابعة»)، والأوّل «بداية المتابعة». يُعرض في «تفاصيل» وفي «تحديث موقف المتابعة».
 */
import type { FollowUp } from '../lib/api.ts';
import { channelAr, priorityAr, resultAr, statusAr, statusTone } from '../lib/labels.ts';
import { cairoStamp, shortDate } from '../lib/format.ts';
import { Badge } from '../components/ui.tsx';

export function CustomerTimeline({ items, currentId, showSeller = false }: {
  items: FollowUp[]; currentId?: string; showSeller?: boolean;
}) {
  if (!items.length) return <p className="text-sm text-ink-500">مفيش تواصل قبل كده مع الرقم ده.</p>;
  const list = [...items].reverse();                               // الأحدث أوّلًا
  return (
    <ol aria-label="سجل العميل" className="space-y-2 border-s-2 border-brand-100 ps-4">
      {list.map((h, i) => (
        <li key={h.id} className="relative">
          <span aria-hidden="true"
            className={`absolute -start-[1.45rem] top-3 h-3 w-3 rounded-full border-2 border-white ${i === 0 ? 'bg-brand-600' : 'bg-slate-300'}`} />
          <div className={`rounded-lg border px-3 py-2 text-sm ${h.id === currentId ? 'border-brand-300 bg-brand-50' : 'border-slate-200 bg-white'}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <bdi className="font-semibold tabular-nums text-ink-900">
                {shortDate(h.contactedOn)}{cairoStamp(h.createdAt) ? ` · ${cairoStamp(h.createdAt)!.time}` : ''}
              </bdi>
              <span className="text-ink-600">{channelAr(h.channel)} · {resultAr(h.result)}</span>
              <Badge tone={statusTone[h.customerStatus]}>{statusAr(h.customerStatus)}</Badge>
              <span className="text-xs font-semibold text-ink-500">{i === list.length - 1 ? 'بداية المتابعة' : `تحديث ${list.length - 1 - i}`}</span>
              {i === 0 && <span className="text-xs font-semibold text-brand-700">الأحدث</span>}
              {h.bookingNo && <Badge tone="bg-emerald-600 text-white">{h.bookingNo}</Badge>}
            </div>
            {h.notes && <p className="mt-1 whitespace-pre-line break-words text-ink-800">{h.notes}</p>}
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-ink-500">
              {h.nextFollowUpOn && <span>المتابعة اللي بعدها: <bdi className="tabular-nums">{shortDate(h.nextFollowUpOn)}</bdi></span>}
              {h.priority !== 'NORMAL' && <span>{priorityAr(h.priority)}</span>}
              {showSeller && <span>{h.salesName}</span>}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}
